import React, { useRef } from 'react';
import classNames from 'classnames';
import Immutable from 'immutable';
import config from 'react-global-configuration';
import { useTranslation } from 'react-i18next';

import { Container } from '@uncinc/uncinc-react-kitchen-sink';

import {
  Node,
  Paragraph,
  Taxonomy,
  View,
} from '@uncinc/react-drupal-core';

import {
  Share,
  Product,
  GetStarted,
  WysiwygField,
  CampaignBlock,
  Hero,
} from '../../../../components';

import Tags from '../../../../components/Tags';
import NodeMetadata from '../../../../components/NodeMetadata';
import NewsletterBlock from '../../../../components/NewsletterBlock';
import ExpertsSlider from '../../../../components/ExpertsSlider';
import MemberWall from '../../../../components/MemberWall';
import useCurrentNodeType from '../../../../helpers/useCurrentNodeType';
import useLoggedInStatus from '../../../../hooks/useLoggedInstatus';

import './style.scss';

const NodeArticleDetail = ({ entity, className }) => {
  const { t } = useTranslation();
  const { isLoggedIn } = useLoggedInStatus();
  const { currentNodeType } = useCurrentNodeType();
  const theme = entity.getIn(['field_theme', 0, 'entity']);
  const created = new Date(Date.parse(entity.getIn(['created', 0, 'value'])));
  const fieldChanged = entity.getIn(['field_changed', 0, 'value'], null);
  const changed = fieldChanged ? new Date(Date.parse(fieldChanged)) : null;
  const title = entity.getIn(['title', 0, 'value'], '');
  const subtitle = entity.getIn(['field_subtitle', 0, 'value']);
  const type = entity.getIn([`field_${currentNodeType}_type`, 0, 'entity', 'name', 0, 'value'], 'Artikel');
  const experts = entity.getIn(['field_experts'], Immutable.Map());
  const author = entity.getIn(['field_auteur'], Immutable.Map());
  const allParagraphs = entity.getIn(['field_paragraphs'], Immutable.Map());
  const product = entity.getIn(['field_commerce_product', 0], null);
  const getStartedContent = entity.getIn(['field_get_started_content'], Immutable.Map()).concat(entity.getIn(['field_get_started_tax_content']));
  const subjects = entity.getIn(['field_subject']);
  const workfields = entity.getIn(['field_workfield']);
  const phasesOfLife = entity.getIn(['field_phase_of_life']);
  const locations = entity.getIn(['field_locations']);
  const heroImage = entity.getIn(['field_media_image', 0], Immutable.Map());
  const campaign = entity.getIn(['field_campaign', 0, 'entity'], null);
  const isMemberOnlyContent = entity.getIn(['field_member_only_content', 0, 'value'], false);
  const classes = classNames(className, 'NodeArticleDetail', heroImage ? 'NodeArticleDetail--hero' : 'NodeArticleDetail--noHero');

  const showMemberWall = !isLoggedIn && isMemberOnlyContent;

  const paragraphs = allParagraphs.filter((item) => {
    const itemType = item.getIn(['entity', 'type', 0, 'target_id']);
    return itemType !== 'cta';
  });

  const ctaParagraphs = allParagraphs.filter((item) => {
    const itemType = item.getIn(['entity', 'type', 0, 'target_id']);
    return itemType === 'cta';
  });

  const getStartedRef = useRef();

  const relatedDossierArgs = entity.getIn(['field_subject'])
    .concat(entity.getIn(['field_workfield']))
    .concat(entity.getIn(['field_phase_of_life']))
    .concat(entity.getIn(['field_locations']));

  const body = entity.getIn(['body', 0, 'processed']);

  const showAuthorList = [
    'Blog',
    'Achtergrond',
  ];

  const expertsTitle = entity.getIn(['field_experts_title', 0, 'value'], t('expert.node_title'));

  return (
    <article typeof="schema:WebPage" className={classes}>
      <Hero
        image={heroImage}
      />

      <Container>
        <div className="NodeArticleDetail__content__wrapper">
          <div className="row">
            <div className={`${(heroImage.size > 0) ? 'col-12 col-lg-10 offset-lg-1' : 'col-12 col-lg-8 offset-lg-2'}`}>
              <div className="NodeArticleDetail__content" id="main">
                <NodeMetadata
                  icon="icon-copy"
                  displayType={type}
                  created={created}
                  theme={theme}
                />
                <h1>{title}</h1>

                {subtitle && (
                  <div className="NodeArticleDetail__subtitle">
                    {subtitle}
                  </div>
                )}

                {body && (
                  <WysiwygField
                    property="schema:text"
                    className="NodeArticleDetail__body subheading2"
                    html={body}
                  />
                )}

                {changed && (
                  <div className="NodeArticleDetail__info">
                    <div className="NodeArticleDetail__changed">
                      Laatste wijziging: {`${changed.getDate()}-${changed.getMonth() + 1}-${changed.getFullYear()}`}
                    </div>
                  </div>
                )}

                {product && (
                  <Product entity={product.get('entity')} />
                )}
              </div>
            </div>
          </div>
        </div>
        {entity.getIn(['type', 0, 'target_id']) !== 'tool' && author.size > 0 && showAuthorList.includes(type) && (
          <div className="row">
            <div className="col-lg-8 offset-lg-2">
              <div className="NodeArticleDetail__experts__wrapper">
                <div className="NodeArticleDetail__experts">
                  {author.map((authorItem) => (
                    <Node
                      key={authorItem.get('uuid')}
                      id={authorItem.getIn(['entity', 'nid', 0, 'value'])}
                      entity={authorItem.get('entity')}
                      viewMode="teaser"
                      isSmall
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </Container>

      <div className="NodeContentWrapper">
        {paragraphs.size > 0 && (
          <div className="Paragraphs">
            {
              paragraphs.map((item, index) => {
                if (showMemberWall && index > 0) return;
                const paragraphId = item.get('target_id');
                const paragraph = item.getIn(['entity']);

                return (
                  <Paragraph
                    key={paragraphId}
                    entity={paragraph}
                    id={paragraphId}
                  />
                );
              })
            }
          </div>
        )}

        {!showMemberWall && (
          <>
            <ExpertsSlider
              expertsTitle={expertsTitle}
              experts={experts}
            />

            {campaign && (
              <CampaignBlock entity={campaign} />
            )}

            <GetStarted
              title={entity.getIn(['field_get_started_title', 0, 'value'])}
              subtitle={entity.getIn(['field_get_started_subtitle', 0, 'value'])}
              content={getStartedContent}
              getStartedRef={getStartedRef}
            />

            {relatedDossierArgs.size > 0 && (
              <div className="TaxonomySubject__related_dossiers">
                <Container>
                  <h2>{entity.getIn(['field_related_dossiers_title', 0, 'value'], 'Dit artikel is onderdeel van')}</h2>
                  <View id="article_dossiers_related" display="block_1" args={relatedDossierArgs} />
                </Container>
              </div>
            )}

            {ctaParagraphs.size > 0 && (
              <div className="CTA__Paragraphs">
                {
                  ctaParagraphs.map((item, index) => {
                    const paragraphId = item.get('target_id');
                    const paragraph = item.getIn(['entity']);

                    return (
                      <Paragraph key={paragraphId} entity={paragraph} id={paragraphId} />
                    );
                  })
                }
              </div>
            )}

            {entity.getIn(['field_paragraph_feedback', 0]) && (
              <div className="NodeArticleDetail__feedback">
                {
                  entity.getIn(['field_paragraph_feedback']).map((item, index) => {
                    const paragraphId = item.get('target_id');
                    const paragraph = item.getIn(['entity']);

                    return (
                      <Paragraph key={paragraphId} entity={paragraph} id={paragraphId} />
                    );
                  })
                }
              </div>
            )}

            <Tags subjects={subjects} workfields={workfields} phasesOfLife={phasesOfLife} locations={locations} />

            {theme && (
              <Container>
                <div className="row">
                  <div className="col-12">
                    <Taxonomy
                      id={theme.getIn(['tid', 0, 'value'])}
                      entity={theme}
                      viewMode="card"
                    />
                  </div>
                </div>
              </Container>
            )}

            <Container>
              <div className="row">
                <div className="col-12 col-lg-8 offset-lg-2">
                  <Share pageTitle={entity.getIn(['title', 0, 'value'], '')} />
                </div>
              </div>
            </Container>
          </>
        )}


      </div>


      {/* Member wall. */}
      {showMemberWall && (
        <MemberWall />
      )}

      {/* Newsletter subscription block */}
      <NewsletterBlock />
    </article>
  );
};

export default NodeArticleDetail;
